import { Box, Divider, Flex, Spinner, Text, useColorModeValue } from '@chakra-ui/react';
import { typography } from 'theme/tokens';
import { useNavigate } from 'react-router-dom';
import { translate } from '../../../i18n';
import {
  READ_FAILED_I18N_KEY,
  scopePromptI18nKey,
  classifyRead,
} from '../../_shared/readState';
import { useSelectedOrganization } from '../../organization/hooks/useSelectedOrganization';
import { useGetAdminNotifications } from '../hooks/useGetAdminNotifications';
import { useMarkAsRead } from '../hooks/useMarkAsRead';
import { useMarkAllAsRead } from '../hooks/useMarkAllAsRead';
import NotificationFeedItem from './NotificationFeedItem';

interface NotificationDropdownProps {
  onClose: () => void;
}

export default function NotificationDropdown({ onClose }: NotificationDropdownProps) {
  const { selectedOrganization } = useSelectedOrganization();
  const orgId = selectedOrganization?.id;
  const { data, isInitialLoading, isError } = useGetAdminNotifications({
    organizationId: orgId,
    limit: 10,
  });
  // This dropdown hangs off the navbar, so it renders in the chrome of EVERY
  // page — including the whole session in which `my-organizations` was refused
  // or came back empty and nothing was ever selected (R100-7). Until run 101 it
  // drove its spinner from the raw `isLoading`, which React Query reports as
  // `true` for the disabled query underneath, so that session saw a spinner
  // that never resolved on every page it opened the bell on.
  const readState = classifyRead(isError, isInitialLoading, data);
  const markAsRead = useMarkAsRead(orgId);
  const markAllAsRead = useMarkAllAsRead(orgId);
  const navigate = useNavigate();

  const textPrimary = useColorModeValue('secondaryGray.900', 'white');
  const textSecondary = useColorModeValue('secondaryGray.600', 'gray.400');
  const borderColor = useColorModeValue('#E6ECFA', 'rgba(135, 140, 189, 0.3)');

  const notifications = data?.notifications ?? [];
  const unreadCount = data?.unreadCount ?? 0;

  return (
    <Box data-testid="notifications-dropdown" w="340px" maxH="420px">
      {/* Header */}
      <Flex justify="space-between" align="center" px="14px" py="10px">
        <Flex align="center" gap="6px">
          <Text {...typography.bodyBold} color={textPrimary}>
            {translate('admin_notifications.title' as any)}
          </Text>
          {unreadCount > 0 && (
            <Flex
              align="center"
              justify="center"
              bg="brand.500"
              color="white"
              borderRadius="full"
              minW="18px"
              h="18px"
              px="5px"
              {...typography.caption}
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </Flex>
          )}
        </Flex>
        {unreadCount > 0 && (
          <Text
            {...typography.caption}
            color="brand.500"
            cursor="pointer"
            _hover={{ textDecoration: 'underline' }}
            onClick={() => markAllAsRead.mutate()}
          >
            {translate('admin_notifications.mark_all_read' as any)}
          </Text>
        )}
      </Flex>

      <Divider borderColor={borderColor} />

      {/* Content */}
      <Box overflowY="auto" maxH="320px" px="4px" py="4px">
        {readState === 'loading' ? (
          <Flex justify="center" py="40px" data-testid="notifications-loading">
            <Spinner size="sm" color="brand.500" />
          </Flex>
        ) : readState === 'unreadable' ? (
          <Flex direction="column" align="center" py="40px" data-testid="notifications-unreadable">
            <Text {...typography.caption} color={textSecondary}>
              {translate(READ_FAILED_I18N_KEY as any)}
            </Text>
          </Flex>
        ) : readState === 'never-issued' ? (
          // Not "No notifications yet": nothing was asked, so the organization
          // having none is not something this dropdown is entitled to claim.
          <Flex direction="column" align="center" py="40px" data-testid="notifications-never-issued">
            <Text {...typography.caption} color={textSecondary}>
              {translate(scopePromptI18nKey('organization') as any)}
            </Text>
          </Flex>
        ) : notifications.length === 0 ? (
          <Flex direction="column" align="center" py="40px" data-testid="notifications-empty">
            <Text {...typography.caption} color={textSecondary}>
              {translate('admin_notifications.empty' as any)}
            </Text>
          </Flex>
        ) : (
          notifications.map(notification => (
            <NotificationFeedItem
              key={notification.id}
              notification={notification}
              onMarkAsRead={(id) => markAsRead.mutate(id)}
              onClose={onClose}
            />
          ))
        )}
      </Box>

      {/* Footer */}
      {notifications.length > 0 && (
        <>
          <Divider borderColor={borderColor} />
          <Flex justify="center" py="8px">
            <Text
              {...typography.caption}
              color="brand.500"
              cursor="pointer"
              _hover={{ textDecoration: 'underline' }}
              onClick={() => {
                navigate('/admin/admin-notifications');
                onClose();
              }}
            >
              {translate('admin_notifications.view_all' as any)}
            </Text>
          </Flex>
        </>
      )}
    </Box>
  );
}
